import React, { useState, useEffect } from 'react';
import TimeTracker from './components/TimeTracker';
import LoginForm from './components/LoginForm';
import AdminPanel from './components/AdminPanel';
import PasswordReset from './components/PasswordReset';
import { SchoolProvider } from './contexts/SchoolContext';

// Esusd School Logo Component - Updated to accept dynamic logoUrl
const EsusdLogo = ({ size = 48, className = "", logoUrl = "/assets/esusd-logo.png" }) => {
  return (
    <img
    src={logoUrl}
    alt="School Logo"
    width={size}
    height={size}
    className="rounded-full"
    />
    );
};

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [currentView, setCurrentView] = useState('timetracker'); // 'timetracker', 'admin', 'login', or 'reset-password'
  const [resetToken, setResetToken] = useState(null);
  const [schools, setSchools] = useState([]);

  useEffect(() => {
    // Check for password reset token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const resetTokenFromUrl = urlParams.get('token');
    
    if (resetTokenFromUrl) {
      setResetToken(resetTokenFromUrl);
      setCurrentView('reset-password');
      // Clear the token from URL for security
      window.history.replaceState({}, document.title, window.location.pathname);
      return; // Don't proceed with normal auth check if we're in reset mode
    }

    if (token) {
      // Verify token and get user info
      fetchUserProfile();
    } else {
      setCurrentView('login');
    }
  }, [token]);

  const fetchUserProfile = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        // Backend now returns schools array with id, name, and logoUrl
        setSchools(userData.schools || []);
        setCurrentView('timetracker');
      } else {
        localStorage.removeItem('token');
        setToken(null);
        setCurrentView('login');
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      localStorage.removeItem('token');
      setToken(null);
      setCurrentView('login');
    }
  };

  const handleLogin = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    setSchools(userData.schools || []);
    localStorage.setItem('token', authToken);
    setCurrentView('timetracker');
  };

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    setSchools([]);
    localStorage.removeItem('token');
    localStorage.removeItem('selectedSchoolId');
    setCurrentView('login');
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  const handleLoginRedirect = () => {
    setCurrentView('login');
    setResetToken(null);
  };

  // Render password reset page
  if (currentView === 'reset-password') {
    return (
      <PasswordReset
        token={resetToken}
        onLoginRedirect={handleLoginRedirect}
        logo={<EsusdLogo size={72} />}
      />
    );
  }

  // Render login page
  if (currentView === 'login' || !user || !token) {
    return (
      <LoginForm
        onLogin={handleLogin}
        logo={<EsusdLogo size={144} />}
      />
    );
  }

  // Render admin panel
  if (currentView === 'admin' && user.role === 'admin') {
    return (
      <SchoolProvider schools={schools}>
        <AdminPanel
          user={user}
          token={token}
          onBack={() => setCurrentView('timetracker')}
        />
      </SchoolProvider>
    );
  }

  // Render main time tracker
  return (
    <SchoolProvider schools={schools}>
      <TimeTracker
        user={user}
        token={token}
        onLogout={handleLogout}
        onViewChange={handleViewChange}
      />
    </SchoolProvider>
  );
}

export default App;